import Queue from "../../../../util/datastructures/queue/Queue";
import EventDispatcher from "../../../../util/EventDispatcher";
import StoryPlayerEvent from "./StoryPlayerEvent";
import StoryPlayerMemento from "./StoryPlayerMemento";
import StoryPlayerState from "./StoryPlayerState";


/**
* The StoryPlayer is usually between a game and the story component. The StoryPlayer is
* the interface to playing a story that has been modelled with the Story, StoryArc and StoryNode interfaces
* and implementations. By using a StoryPlayer you can create a 'instance' of the story for your game (on the story level, not
* code level) to play it to the user. This way the state of the IStory for a certain game is separated from the modelled state. 
* This makes it easier to create save games and allows for multiple 'instances' of the IStory.
* 
* <p>Playing the story is done in a few steps:
* 
* <ul>
*  <li>Initialize the Story and Storyplayer</li>
*  <li>Start listening to events of the storyplayer and map them to actions</li>
*  <li>Call the start function to start the storyplayer</li>
*  <li>Calling evaluate to let the story logic evaluate the story logic</li>
*  <li>The StoryPlayer dispatches events if something changed in the story state, e.g. a StoryNode of StoryArc has
*    become available, unavailable</li>
*  <li>The game can tell the StoryPlayer that a certain StoryNode is completed (e.g. a dialog is finished)</li>
* </ul>
* 
* </p>
* 
* <p><b>About loading and saving:</b> the StoryPlayer object is an object that contains state while the IStory model does not
* contain any state specific for a game. The IStory model only contains an abstraction of the structure of the story but typically
* isn't changed during a game (allthough it could be changed but then you are on your own). The StoryPlayer does not contain any modelling but
* only state related to the model. On an abstract view the state of the player contains: the available-, unavailable- and
* completed StoryArcs and StoryNodes.</p>
* 
* <p><b>About timing:</b> when calling the evaluate function you have to supply a current time in the form
* of a number value. The story engine works with this number as the time unit but doesn't interpet this
* to a certain time unit in real time (e.g. 1 minute, second or millisecond). The story arcs and story
* nodes can have a timing condition also based on this time unit. By letting the game supply the current
* time you can have your own in game clock and e.g. make sure time stands still when the player isn't in
* the game, but also choose a different implementation like using real time.</p>
* 
* @constructor
* @param {Story} story The Story for the StoryPlayer
* @param {Solver} solver The Solver to use for variable solving
* 
**/
export default class StoryPlayer extends EventDispatcher {
    constructor(story, solver) {
        super();
        this._solver = solver;
        this._eventQueue = new Queue();
        this._state = new StoryPlayerState();
        this._story = story;
    }

    /**
     * Get the story for this StoryPlayer
     * @returns {Story}
     */
    getStory() {
        return this._story;
    }

    /**
     * Get the solver function for the StoryPlayer
     * @returns {Function}
     */
    getSolver() {
        return this._solver;
    }

    /**
     * Set the solver function for the StoryPlayer
     * @param {type} val
     * @returns {undefined}
     */
    setSolver(val) {
        this._solver = val;
    }

    /**
     * Start playing the story. Note that you should not call this function more than once and not use it
     * to start the player in the sence of starting a Timer. If you load in a previous state the story is allready
     * started
     * @param {number} time
     * @returns {undefined}
     */
    start(time) {
        if (!this.isStarted()) {
            this._state.start(time);
            //Dispatch the start event
            this.addEvent(StoryPlayerEvent.STORY_START);
            //TODO: do we want to send events here or only in the evaluate?
            this.dispatchEvents();
        } else {
            throw new Error("Story is already started");
        }
    }

    /**
     * Returns whether the IStoryPlayer is started 
     * @returns {unresolved}
     */
    isStarted() {
        return this._state.isStarted();
    }

    /**
     * Tells the StoryPlayer that a certain StoryNode has been completed
     * @param {StoryNode} storyNode The IStoryNode that is completed
     * @param {number} time The current time
     * @param {*} result A result object that is stored with the StoryNode and can be referenced later on
     * @returns {undefined}
     */
    complete(storyNode, time, result) {
        //Check if the storyNode was available
        if (!this.isStoryNodeCompleted(storyNode)) {
            if (this.isStoryNodeAvailable(storyNode)) {
                //           LogUtils.getLogger(this).info("StoryNode '"+storyNode.data+"' is completed");

                //Complete actions				
                this._state.storyNodeCompleted(storyNode, time, result);
                this.addEvent(StoryPlayerEvent.STORY_NODE_COMPLETED, storyNode.getStoryArc(), storyNode);
                //Make the story node unavailable	
                if (!storyNode.isRetained()) {
                    this.storyNodeBecomesUnAvailable(storyNode, time);
                }
                //Complete the storyarc if this node is an end node
                if (storyNode.isEnd()) {
                    this.storyArcBecomesCompleted(storyNode.getStoryArc(), time);
                }
            } else {
                throw new Error(`You can't complete a StoryNode that is not available (${storyNode.id})`);
            }
        } else {
            throw new Error(`StoryNode is already completed (${storyNode.id})`);
        }
        //TODO: do we want to send events here or only in the evaluate?
        this.dispatchEvents();
    }

    /**
     * Let the IStoryPlayer evaluate actions. 
     * @param {number} time
     * @returns {undefined}
     */
    evaluate(time) {
        let storyArc;

        //    LogUtils.getLogger(this).debug("Evaluating time: "+time);

        //Step 1: evaluate if there are storyarcs that become unlocked
        const arcItr = this._story.getStoryArcs();
        while (arcItr.hasNext()) {
            storyArc = arcItr.next();
            if (!this.isStoryArcUnlocked(storyArc)) {
                if (storyArc.evaluateUnlockCondition(this._solver)) {
                    this.storyArcBecomesUnlocked(storyArc, time);
                }
            }
        }

        //Step 2: evaluate availability of StoryArcs
        const unlockedArcItr = this.unlockedStoryArcs();
        while (unlockedArcItr.hasNext()) {
            storyArc = unlockedArcItr.next();
            if (this.isStoryArcAvailable(storyArc)) {
                if (!storyArc.evaluateAvailableCondition(this._solver)) {
                    this.storyArcBecomesUnAvailable(storyArc, time);
                }
            } else {
                if ((!this.isStoryArcCompleted(storyArc) || storyArc.isRetained()) && storyArc.evaluateAvailableCondition(this._solver)) {
                    this.storyArcBecomesAvailable(storyArc, time);
                }
            }
        }

        //Step 3: evaluate available Story Arcs
        const availableArcItr = this.availableStoryArcs();
        while (availableArcItr.hasNext()) {
            storyArc = availableArcItr.next();
            this.evaluateStoryArc(storyArc, time);
        }

        //Dispatch events
        this.dispatchEvents();
    }

    /**
     * @private
     * @param {StoryArc} storyArc
     * @param {number} time
     * @returns {undefined}
     */
    evaluateStoryArc(storyArc, time) {
        let storyNode;
        //Step 1: Check unlock
        const storyNodeItr = storyArc.getStoryNodes();
        while (storyNodeItr.hasNext()) {
            storyNode = storyNodeItr.next();
            if (!this.isStoryNodeUnlocked(storyNode)) {
                if (storyNode.evaluateUnlockCondition(this._solver)) {
                    this.storyNodeBecomesUnlocked(storyNode, time);
                }
            }
        }

        //Step 2: Check availability
        const unlockedStoryNodeItr = storyArc.getStoryNodes();
        while (unlockedStoryNodeItr.hasNext()) {
            storyNode = unlockedStoryNodeItr.next();
            if (this.isStoryNodeUnlocked(storyNode)) {
                if (this.isStoryNodeAvailable(storyNode)) {
                    if (!storyNode.evaluateAvailableCondition(this._solver)) {
                        this.storyNodeBecomesUnAvailable(storyNode, time);
                    }
                } else {
                    if ((!this.isStoryNodeCompleted(storyNode) || storyNode.isRetained()) && storyNode.evaluateAvailableCondition(this._solver)) {
                        this.storyNodeBecomesAvailable(storyNode, time);
                    }
                }
            }
        }
    }

    /**
     * @private 
     * @param {StoryArc} storyArc
     * @param {number} time
     * @returns {undefined}
     */
    storyArcBecomesUnlocked(storyArc, time) {
        this._state.unlockStoryArc(storyArc, time);
        //        LogUtils.getLogger(this).info("StoryArc '"+storyArc.label+"' becomes unlocked");
        this.addEvent(StoryPlayerEvent.STORY_ARC_UNLOCKED, storyArc);
    }

    /**
     * @private
     * @param {StoryNode} storyNode
     * @param {number} time
     * @returns {undefined}
     */
    storyNodeBecomesUnlocked(storyNode, time) {
        this._state.unlockStoryNode(storyNode, time);
        //      LogUtils.getLogger(this).info("StoryNode '"+storyNode.label+"' becomes unlocked");
        console.info(`StoryNode '${storyNode.getLabel()}' becomes unlocked`);
        this.addEvent(StoryPlayerEvent.STORY_NODE_UNLOCKED, storyNode.getStoryArc(), storyNode);
    }

    /**
     * @private
     * @param {StoryArc} storyArc  The StoryArc that has become available
     * @param {number} time
     **/
    storyArcBecomesAvailable(storyArc, time) {
        this._state.storyArcAvailable(storyArc, time);
        //        LogUtils.getLogger(this).info("StoryArc '"+storyArc.label+"' becomes available");
        this.addEvent(StoryPlayerEvent.STORY_ARC_AVAILABLE, storyArc);
    }

    /**
     * @private
     * @param {StoryArc} storyArc
     * @param {number} time
     * @returns {undefined}
     */
    storyArcBecomesUnAvailable(storyArc, time) {
        //First make all storynodes in the story arc that are available unavailable
        const storyNodeItr = storyArc.getStoryNodes();
        while (storyNodeItr.hasNext()) {
            const storyNode = storyNodeItr.next();
            if (this._state.isStoryNodeAvailable(storyNode)) {
                this.storyNodeBecomesUnAvailable(storyNode, time);
            }
        }
        //Remove the storyarc
        this._state.storyArcUnAvailable(storyArc, time);
        //       LogUtils.getLogger(this).info("storyArc '"+storyArc.label+"' becomes unavailable");
        this.addEvent(StoryPlayerEvent.STORY_ARC_UNAVAILABLE, storyArc);
    }

    /**
     * @private
     * @param {StoryArc} storyArc
     * @param {number} time
     * @returns {undefined}
     */
    storyArcBecomesCompleted(storyArc, time) {
        this._state.storyArcCompleted(storyArc, time);
        //        LogUtils.getLogger(this).info("StoryArc '"+storyArc.label+"' becomes completed");

        if (!storyArc.isRetained()) {
            this.storyArcBecomesUnAvailable(storyArc, time);
        }
        this.addEvent(StoryPlayerEvent.STORY_ARC_COMPLETED, storyArc);

        if (storyArc.isEnd()) {
            this.storyEnd();
        }
    }

    /**
     * @private
     * @returns {undefined}
     */
    storyEnd() {
        this.addEvent(StoryPlayerEvent.STORY_END);
    }

    /**
     * @private
     * @param {StoryNode} storyNode
     * @param {number} time
     * @returns {undefined}
     */
    storyNodeBecomesAvailable(storyNode, time) {
        this._state.storyNodeAvailable(storyNode, time);
        //      LogUtils.getLogger(this).info("StoryNode '"+storyNode.label+"' becomes available");
        console.info(`StoryNode '${storyNode.getLabel()}' becomes available`);
        this.addEvent(StoryPlayerEvent.STORY_NODE_AVAILABLE, storyNode.getStoryArc(), storyNode);
    }

    /**
     * @private
     * @param {StoryNode} storyNode
     * @param {number} time
     * @returns {undefined}
     */
    storyNodeBecomesUnAvailable(storyNode, time) {
        this._state.storyNodeUnAvailable(storyNode, time);
        //        LogUtils.getLogger(this).info("StoryNode '"+storyNode.label+"' becomes unavailable");			
        console.info(`StoryNode '${storyNode.getLabel()}' becomes unavailable`);
        this.addEvent(StoryPlayerEvent.STORY_NODE_UNAVAILABLE, storyNode.getStoryArc(), storyNode);
    }

    /**
     * 
     * @param {StoryPlayerMemento} storyPlayerMemento
     * @returns {undefined}
     */
    load({ data }) {
        //        LogUtils.getLogger(this).info("loading state");			
        const input = data;
        this._state.fromObj(input, this._story);
        //       LogUtils.getLogger(this).info("finished loading state");			
    }

    /**
     * 
     * @returns {StoryPlayerMemento}
     */
    save() {
        //        LogUtils.getLogger(this).debug("save called");			
        return new StoryPlayerMemento(this._state.toObj());
    }

    /**
     * Enqueue an Event for dispatching
     * @private
     * @param {String} type
     * @param {StoryArc} storyArc
     * @param {StoryNode} storyNode
     * @returns {undefined}
     */
    addEvent(type, storyArc, storyNode) {
        this._eventQueue.enqueue({ type, storyArc, storyNode });
    }

    /**
     * @private
     * @returns {undefined}
     */
    dispatchEvents() {
        //TODO: implement
        while (!this._eventQueue.isEmpty()) {
            const evt = this._eventQueue.dequeue();
            this.dispatchEvent(evt.type, { storyArc: evt.storyArc, storyNode: evt.storyNode }, this);
        }
    }

    /**
     * Returns whether the StoryArc is unlocked
     * @param {StoryArc} storyArc
     * @returns {Boolean}
     */
    isStoryArcUnlocked(storyArc) {
        return this._state.isStoryArcUnlocked(storyArc);
    }

    /**
     * Returns whether the StoryArc is available
     * @param {StoryArc} storyArc
     * @returns {Boolean}
     */
    isStoryArcAvailable(storyArc) {
        return this._state.isStoryArcAvailable(storyArc);
    }

    /**
     * Returns whether the StoryArc is completed
     * @param {StoryArc} storyArc
     * @returns {Boolean}
     */
    isStoryArcCompleted(storyArc) {
        return this._state.isStoryArcCompleted(storyArc);
    }

    /**
     * Returns whether the StoryNode is unlocked
     * @param {StoryNode} storyNode
     * @returns {Boolean}
     */
    isStoryNodeUnlocked(storyNode) {
        return this._state.isStoryNodeUnlocked(storyNode);
    }

    /**
     * Returns whether the StoryNode is available
     * @param {StoryNode} storyNode
     * @returns {Boolean}
     */
    isStoryNodeAvailable(storyNode) {
        return this._state.isStoryNodeAvailable(storyNode);
    }

    /**
     * Returns whether the StoryNode is completed
     * @param {StoryNode} storyNode
     * @returns {Boolean}
     */
    isStoryNodeCompleted(storyNode) {
        return this._state.isStoryNodeCompleted(storyNode);
    }

    /**
     * Returns an iterator for all the StoryArcs that are unlocked at the current state.
     * @returns {Iterator}
     */
    unlockedStoryArcs() {
        return this._state.unlockedStoryArcs();
    }

    /**
     * Returns an iterator for all the StoryArcs that are available at the current state.
     * @returns {Iterator}
     */
    availableStoryArcs() {
        return this._state.availableStoryArcs();
    }

    /**
     * Returns an iterator for all the StoryArcs that are completed at the current state.
     * @returns {Iterator}
     */
    completedStoryArcs() {
        return this._state.completedStoryArcs();
    }

    /**
     * Returns an iterator for all the StoryNodes that are unlocked at the current state.
     * @returns {Iterator}
     */
    unlockedStoryNodes() {
        return this._state.unlockedStoryNodes();
    }

    /**
     * Returns an iterator for all the StoryNodes that are available at the current state.
     * @returns {Iterator}
     */
    availableStoryNodes() {
        return this._state.availableStoryNodes();
    }

    /**
     * Returns an iterator for all the StoryNodes that are completed at the current state.
     * @returns {Iterator}
     */
    completedStoryNodes() {
        return this._state.completedStoryNodes();
    }

    /**
     * Get the result of a StoryNode that was stored upon completion of the StoryNode. If the
     * StoryNode was not completed yet null is returned. Note that null is also returned when
     * no result is set so don't use a null return as an indicator that the storyNode was not completed, use
     * isStoryNodeCompleted instead.
     * @param {StoryNode} storyNode
     * @returns {*}
     */
    getResult(storyNode) {
        return this._state.getResult(storyNode);
    }

    /**
     * Returns the time that a StoryNode was unlocked. 
     * @param {StoryNode} storyNode
     * @returns {number}
     */
    storyNodeUnlockedTime(storyNode) {
        return this._state.storyNodeUnlockedTime(storyNode);
    }

    /**
     * Returns the time that a StoryNode became avialable.
     * @param {StoryNode} storyNode
     * @returns {number}
     */
    storyNodeAvailableTime(storyNode) {
        return this._state.storyNodeAvailableTime(storyNode);
    }

    /**
     * Returns the time that a StoryNode was completed. 
     * @param {StoryNode} storyNode
     * @returns {number}
     */
    storyNodeCompletedTime(storyNode) {
        return this._state.storyNodeCompletedTime(storyNode);
    }

    /**
     * Returns the time that a StoryArc was unlocked. 
     * @param {StoryArc} storyArc
     * @returns {number}
     */
    storyArcUnlockedTime(storyArc) {
        return this._state.storyArcUnlockedTime(storyArc);
    }

    /**
     * Returns the time that a StoryArc became available.
     * @param {StoryArc} storyArc
     * @returns {number}
     */
    storyArcAvailableTime(storyArc) {
        return this._state.storyArcAvailableTime(storyArc);
    }

    /**
     * Returns the time that a StoryArc was completed.
     * @param {StoryArc} storyArc
     * @returns {number}
     */
    storyArcCompletedTime(storyArc) {
        return this._state.storyArcCompletedTime(storyArc);
    }

    /**
     * Returns the start time of the Story.
     * @returns {number}
     */
    getStoryStartTime() {
        return this._state.getStoryStartTime();
    }
}


